import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const feteDuJasminGrasseEn: ChauffeurPage = {
  slug: "fete-du-jasmin-grasse",
  title: "Private chauffeur Fête du Jasmin Grasse – VIP VTC",
  subtitle:
    "Private chauffeur during the Fête du Jasmin in Grasse: access to the flower parade, shuttles from the coast and Nice airport, parking avoided in the old town.",
  keywords: [
    "fete du jasmin grasse chauffeur",
    "jasmine festival grasse chauffeur",
    "grasse flower parade vtc",
  ],
  hero: {
    title: "Private chauffeur during the Fête du Jasmin in Grasse",
    subtitle:
      "A flower parade in early August in the perfume capital: a dedicated chauffeur when Grasse's streets close and parking runs out.",
    keywords: [
      "Fête du Jasmin chauffeur",
      "Grasse VTC",
      "flower parade shuttle",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Fête du Jasmin in Grasse",
    badges: ["Shuttles from the coast", "Old-town drop-off", "Evening returns"],
  },
  benefits: {
    heading: "Why a chauffeur during the Fête du Jasmin",
    items: [
      {
        title: "Old town, closed streets",
        description:
          "The flower parade crosses central Grasse: barred roads and a pedestrian old town. The chauffeur drops you as close as possible to the authorised access points.",
      },
      {
        title: "Grasse parking avoided",
        description:
          "Perched and dense, Grasse has few spaces, even fewer during the festival. Drop-off and pick-up with no hunt for a space.",
      },
      {
        title: "Shuttles from the coast",
        description:
          "From Cannes, Nice or the airport, direct links to Grasse for the jasmine weekend, with no road or parking worries.",
      },
      {
        title: "Evening returns secured",
        description:
          "Parade, entertainment and dinners: the chauffeur waits and secures the return to your hotel or villa.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Fête du Jasmin",
    services: [
      {
        title: "Shuttles from the coast",
        text: "Cannes, Nice or airport ↔ Grasse links for the Fête du Jasmin.",
      },
      {
        title: "Nice airport meet-and-greet",
        text: "Transfer with flight tracking to Grasse and its hinterland.",
      },
      {
        title: "Drop-off nearest the parade",
        text: "Approach to the authorised access points within the streets closed for the flower parade.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to combine the festival, perfumery visits and lunches.",
      },
      {
        title: "Evenings & dinners",
        text: "Grasse and hinterland restaurants, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Fête du Jasmin",
    items: [
      {
        name: "Grasse old town",
        description:
          "Lanes and squares of the historic centre, route of the flower parade.",
      },
      {
        name: "Cours Honoré Cresp & perfumeries",
        description: "Heart of the perfume capital and its emblematic houses.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, the gateway from the airport.",
      },
      {
        name: "Cannes & coastline",
        description: "Links from the coast to Grasse for the day.",
      },
      {
        name: "Grasse hinterland",
        description: "Villas, estates and restaurants around Grasse.",
      },
    ],
  },
  generic: {
    heading: "The Fête du Jasmin from a chauffeur's seat",
    subheading:
      "In early August, Grasse celebrates jasmine with a flower parade that crosses and closes the old town.",
    paragraphs: [
      "Every early August, the Fête du Jasmin animates Grasse, the perfume capital: a flower parade, floats decorated with blooms, music and jasmine handed out to the crowd. The parade crosses the old town and closes several streets; a chauffeur who knows the authorised access points gets you close without losing you in the lanes.",
      "Grasse is perched and its parking, already scarce, saturates during the festival. From Cannes, Nice or Nice Côte d'Azur airport, the chauffeur handles the link and drops you as close as possible, then collects you on the way out, with no hunt for a space.",
      "Between the parade, the entertainment, perfumery visits and dinners, hourly hire keeps the same chauffeur through the day and secures the evening returns to your hotel or villa.",
    ],
    bullets: [
      "Drop-off nearest the parade",
      "Shuttles from the coast",
      "Grasse parking avoided",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Fête du Jasmin",
    steps: [
      "Tell us your starting point (coast, airport, hotel) and your times for the weekend.",
      "We plan the link to Grasse around the streets closed for the flower parade.",
      "The chauffeur drops you as close as possible to the access points and collects you on the way out.",
      "Hourly hire available to combine the festival, perfumeries and dinners.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur for the Fête du Jasmin in Grasse?",
      a: "Book in advance: in early August, Grasse is very busy for the flower parade. Share your starting point, your times and your hotel for a dedicated chauffeur across the weekend.",
    },
    {
      q: "Can you drop me as close as possible to the flower parade?",
      a: "Yes. The parade closes several streets in the old town. The chauffeur drops you at the nearest authorised point, then collects you on the way out, with no need to park.",
    },
    {
      q: "Do you offer shuttles from the coast?",
      a: "Yes. From Cannes, Nice or Nice Côte d'Azur airport, we run direct links to Grasse for the Fête du Jasmin.",
    },
    {
      q: "Is parking difficult in Grasse during the festival?",
      a: "Yes. Grasse is perched and spaces are scarce, even more so during the parade. The chauffeur spares you the parking hunt: drop-off and pick-up at authorised points.",
    },
    {
      q: "Can we combine the festival with a perfumery visit?",
      a: "Yes. On hourly hire, the chauffeur chains the parade, visits to the perfume houses and lunches, at your pace.",
    },
    {
      q: "Why choose KS Rent Services for the Fête du Jasmin?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: punctuality and knowledge of Grasse and its hinterland.",
    },
  ],
  relatedLinks: [
    { label: "Car service in Grasse", href: "/en/chauffeur/grasse" },
    { label: "Book a chauffeur in Cannes", href: "/en/chauffeur/cannes" },
    { label: "Book an airport transfer", href: "/en/chauffeur/airport" },
    {
      label: "Car hire in Grasse",
      href: "/en/location/car-rental-grasse",
    },
  ],
  cta: {
    title: "Your chauffeur for the Fête du Jasmin",
    text: "Shuttles from the coast, parade drop-off and evening returns: book a dedicated chauffeur for Grasse.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Fête du Jasmin Grasse Chauffeur — VIP car service",
    description:
      "Private chauffeur during the Fête du Jasmin in Grasse: shuttles from the coast and Nice airport, flower-parade drop-off and parking avoided. Book now.",
    canonical: "/en/chauffeur/fete-du-jasmin-grasse",
  },
};

export default feteDuJasminGrasseEn;
